import React, { useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import { Lock, LogIn, ShieldCheck, Mail, Loader2, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API_BASE } from "../lib/api";

function MagicLinkForm() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setState("sending");
    try {
      const r = await fetch(`${API_BASE}/auth/magic-link`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Please enter a valid email address.");
      setMsg(d.message);
      setState("sent");
    } catch (ex) {
      setMsg(ex.message);
      setState("error");
    }
  };
  if (state === "sent") {
    return (
      <div data-testid="magic-link-sent" className="mt-6 rounded-xl bg-emerald-500/15 border border-emerald-400/40 text-emerald-50 p-4 text-sm flex gap-2">
        <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> <span>{msg} Check your inbox (and spam folder).</span>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="mt-6 space-y-3">
      <label className="block text-xs font-semibold text-emerald-100/80" htmlFor="admin-email">Admin email</label>
      <input id="admin-email" data-testid="admin-login-email-input" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder="you@greenflowlandcare.com" className="w-full h-12 rounded-xl bg-white/95 px-4 text-[#092C15] placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-emerald-400" />
      {state === "error" && <div data-testid="magic-link-error" className="text-xs text-rose-200">{msg}</div>}
      <button data-testid="admin-login-magic-button" type="submit" disabled={state === "sending"}
        className="w-full h-12 rounded-xl bg-emerald-500 text-[#092C15] hover:bg-emerald-400 font-semibold inline-flex items-center justify-center gap-2 transition-colors">
        {state === "sending" ? <Loader2 size={18} className="animate-spin" /> : <Mail size={18} />} Email Me a Sign-In Link
      </button>
    </form>
  );
}

export default function AdminLogin() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-[100dvh] gf-gradient-dark grid place-items-center text-emerald-200">
        Loading…
      </div>
    );
  }
  if (user?.is_admin) return <Navigate to="/admin" replace />;

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const deniedEmail = location.state?.deniedEmail || (user && !user.is_admin ? user.email : null);

  return (
    <div className="min-h-[100dvh] gf-gradient-dark gf-grain">
      <div className="mx-auto max-w-md px-5 pt-16 pb-10 gf-fade-in">
        <div className="rounded-3xl bg-white/5 border border-white/10 p-6 backdrop-blur-md">
          <div className="h-12 w-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 grid place-items-center">
            <ShieldCheck className="text-emerald-300" size={22} />
          </div>
          <div className="mt-5 text-[11px] uppercase tracking-widest font-bold text-emerald-300">
            GreenFlow Admin
          </div>
          <h1 className="mt-1 font-display text-3xl font-extrabold text-white leading-tight">
            Admin Review
          </h1>
          <p className="mt-2 text-emerald-100/80 text-sm">
            Enter the GreenFlow admin email and we'll send you a secure one-time
            sign-in link. No password needed.
          </p>

          {deniedEmail && (
            <div
              data-testid="admin-login-denied-banner"
              className="mt-5 rounded-xl bg-rose-500/15 border border-rose-400/40 text-rose-100 p-3 text-sm"
            >
              <div className="font-semibold flex items-center gap-2">
                <Lock size={14} /> Access Restricted
              </div>
              <div className="mt-1 opacity-90">
                <code className="text-rose-50">{deniedEmail}</code> is not
                authorized. Only the GreenFlow owner account can access the admin
                review.
              </div>
            </div>
          )}

          <MagicLinkForm />
          <button
            data-testid="admin-login-google-button"
            onClick={startLogin}
            className="mt-4 w-full h-11 rounded-xl bg-white/10 border border-white/20 text-white hover:bg-white/15 text-sm font-semibold inline-flex items-center justify-center gap-2"
          >
            <LogIn size={16} />
            Or sign in with Google
          </button>
          <p className="mt-3 text-[11px] text-center text-emerald-100/60">
            Access limited to the GreenFlow owner account.
          </p>
        </div>
      </div>
    </div>
  );
}
